<template>
  <div class="attendance-table-scroll">
    <div v-if="loading" class="attendance-table-loader">
      <oxd-loading-spinner />
    </div>
    <table v-else class="attendance-breakdown-table">
      <thead>
        <tr>
          <th
            v-for="(header, colIndex) in headers"
            :key="header.name"
            :class="headerCellClass(colIndex)"
          >
            {{ header.title }}
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="(row, rowIndex) in items"
          :key="row.id ?? rowIndex"
          :class="{
            'attendance-breakdown-table__total-row': row.dayType === 'TOTAL',
          }"
        >
          <td
            v-for="(header, colIndex) in headers"
            :key="header.name"
            :class="bodyCellClass(colIndex)"
          >
            <component
              :is="cellComponent(header, row, rowIndex)"
              v-bind="cellProps(header, row, rowIndex)"
            />
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import {h} from 'vue';
import {OxdSpinner} from '@ohrm/oxd';

const DayNoteIconCell = {
  props: {
    note: {
      type: String,
      default: '',
    },
  },
  render() {
    if (!this.note) {
      return h('span', {class: 'day-note-empty'}, '');
    }
    return h(
      'span',
      {
        class: 'day-note-icon',
        title: this.note,
      },
      'ⓘ',
    );
  },
};

const TruncatedNoteCell = {
  props: {
    text: {
      type: String,
      default: '',
    },
  },
  computed: {
    displayText() {
      if (!this.text || this.text.length <= 10) {
        return this.text || '';
      }
      return `${this.text.slice(0, 10)}...`;
    },
  },
  render() {
    return h(
      'span',
      {
        class: 'note-truncate',
        title: this.text || '',
      },
      this.displayText,
    );
  },
};

const TextCell = {
  props: {
    text: {
      type: [String, Number],
      default: '',
    },
  },
  render() {
    return h('span', {}, String(this.text ?? ''));
  },
};

const numericColumnIndexes = new Set([6, 7, 8, 9, 10, 11, 14]);
const centerColumnIndexes = new Set([0]);

export default {
  name: 'PayslipAttendanceBreakdownTable',
  components: {
    'oxd-loading-spinner': OxdSpinner,
    DayNoteIconCell,
    TruncatedNoteCell,
    TextCell,
  },
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      headers: [
        {name: 'dayNote', title: ''},
        {name: 'workDate', title: 'Date'},
        {name: 'dayName', title: 'Day'},
        {name: 'punchInTime', title: 'Clock In'},
        {name: 'punchOutTime', title: 'Clock Out'},
        {name: 'dayType', title: 'Day Type'},
        {name: 'attendanceFraction', title: 'WF'},
        {name: 'leaveFraction', title: 'LF'},
        {name: 'holidayFraction', title: 'HF'},
        {name: 'payableFraction', title: 'PF'},
        {name: 'lateTier', title: 'Late deduction'},
        {name: 'lateMinutes', title: 'Late Minutes'},
        {name: 'latePermission', title: 'Late Permission'},
        {name: 'earlyLeavePermission', title: 'Early Leave Permission'},
        {name: 'duration', title: 'Duration (Hours)'},
        {name: 'punchInNote', title: 'In Note'},
        {name: 'punchOutNote', title: 'Out Note'},
      ],
    };
  },
  methods: {
    headerCellClass(colIndex) {
      return {
        'attendance-breakdown-table__cell--numeric':
          numericColumnIndexes.has(colIndex),
        'attendance-breakdown-table__cell--center':
          centerColumnIndexes.has(colIndex),
      };
    },
    bodyCellClass(colIndex) {
      return this.headerCellClass(colIndex);
    },
    cellComponent(header, row, rowIndex) {
      if (header.name === 'dayNote') {
        return DayNoteIconCell;
      }
      if (header.name === 'punchInNote' || header.name === 'punchOutNote') {
        return TruncatedNoteCell;
      }
      if (typeof header.cellRenderer === 'function') {
        const rendered = header.cellRenderer(
          rowIndex,
          row[header.name],
          header,
          row,
        );
        if (rendered?.component) {
          return rendered.component;
        }
      }
      return TextCell;
    },
    cellProps(header, row, rowIndex) {
      if (header.name === 'dayNote') {
        return {note: row.dayNote || ''};
      }
      if (header.name === 'punchInNote' || header.name === 'punchOutNote') {
        return {text: row[header.name] || ''};
      }
      if (typeof header.cellRenderer === 'function') {
        const rendered = header.cellRenderer(
          rowIndex,
          row[header.name],
          header,
          row,
        );
        if (rendered?.props) {
          return rendered.props;
        }
      }
      return {text: row[header.name] ?? ''};
    },
  },
};
</script>

<style lang="scss" scoped>
.attendance-table-scroll {
  width: 100%;
  max-width: 100%;
  min-width: 0;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;
  scrollbar-color: #9ca3af transparent;
  border: 1px solid $oxd-interface-gray-lighten-2-color;
  border-radius: 4px;
  box-sizing: border-box;
}

.attendance-table-scroll::-webkit-scrollbar {
  height: 8px;
}

.attendance-table-scroll::-webkit-scrollbar-track {
  background: transparent;
}

.attendance-table-scroll::-webkit-scrollbar-thumb {
  background-color: #9ca3af;
  border-radius: 4px;
}

.attendance-table-loader {
  display: flex;
  justify-content: center;
  padding: 2rem;
}

.attendance-breakdown-table {
  width: 100%;
  min-width: 1480px;
  border-collapse: collapse;
  table-layout: auto;
  background-color: $oxd-white-color;
  font-family: sans-serif;
  font-size: 12px;
  color: $oxd-interface-gray-darken-1-color;

  th,
  td {
    padding: 0.65rem 0.5rem;
    border-top: 1px solid $oxd-interface-gray-lighten-2-color;
    vertical-align: middle;
    white-space: nowrap;
  }

  thead th {
    font-weight: 700;
    background-color: $oxd-background-light-gray-color;
    border-top: 0;
  }

  tbody tr:hover:not(.attendance-breakdown-table__total-row) {
    background-color: $oxd-background-light-gray-color;
  }

  &__total-row {
    font-weight: 700;
    background-color: $oxd-background-light-gray-color;
  }

  &__cell--numeric {
    text-align: right;
  }

  &__cell--center {
    text-align: center;
  }
}

::v-deep(.day-note-icon) {
  cursor: pointer;
  color: #2563eb;
  font-size: 0.9rem;
}

.day-note-empty {
  display: inline-block;
  width: 0.9rem;
}

.note-truncate {
  cursor: pointer;
}
</style>
